import type { CameraKeyframe } from "@world/Camera";
import * as THREE from "three";

/**
 * Base class for every "scene" of the scroll journey.
 *
 * A scene is NOT a Three.js Scene — it's a logical chunk of the world (the hero terrain,
 * the philosophy voronoi, the carousel of dishes, etc.) that lives inside the master
 * `THREE.Scene` as a `THREE.Group`. Multiple scenes coexist in the same 3D space; the
 * camera travels between them along the spline.
 *
 * Lifecycle:
 *   1. constructor — register the group with the World.
 *   2. init()       — build geometry/materials (async-safe).
 *   3. enter()      — called when scroll progress enters this scene's range.
 *   4. update(dt, p) — called every frame while active. `p` is local progress in [0, 1].
 *   5. leave()      — called when scroll progress exits.
 *   6. dispose()    — geometry/material/texture cleanup.
 */
export abstract class SceneBase {
  readonly group = new THREE.Group();
  readonly id: string;
  /** The camera keyframe(s) this scene contributes to the global path */
  abstract readonly keyframes: CameraKeyframe[];
  /** Scroll progress range this scene is active in, normalized to journey total */
  abstract readonly range: [number, number];

  protected _active = false;
  protected disposers: Array<() => void> = [];

  get isActive(): boolean {
    return this._active;
  }

  constructor(id: string) {
    this.id = id;
    this.group.name = id;
  }

  abstract init(): void | Promise<void>;

  enter(): void {
    this._active = true;
    this.group.visible = true;
  }

  leave(): void {
    this._active = false;
    // Keep group visible by default — camera leaves it behind in 3D space.
    // Scenes that prefer fade-out should override.
  }

  /**
   * Called every frame.
   * @param deltaSeconds time since last frame in seconds
   * @param globalProgress normalized scroll progress over the full journey [0, 1]
   * @param localProgress progress within this scene's range [0, 1], or undefined if outside
   */
  abstract update(
    deltaSeconds: number,
    globalProgress: number,
    localProgress: number | undefined,
  ): void;

  dispose(): void {
    this.disposers.forEach((d) => d());
    this.disposers.length = 0;

    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
        const mat = obj.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mat)) {
          mat.forEach((m) => m.dispose());
        } else {
          mat?.dispose();
        }
      }
    });
    this.group.removeFromParent();
  }

  /** Map global scroll progress into local [0, 1] within this scene's range */
  protected getLocalProgress(globalProgress: number): number | undefined {
    const [a, b] = this.range;
    if (globalProgress < a || globalProgress > b) return undefined;
    return (globalProgress - a) / (b - a);
  }
}
